import { Button } from "design-system/atoms/Button"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "design-system/molecules/DropdownMenu"
import { formatDate, isSameDay } from "lopermut"
import { Bot, BotOff, ChevronUp } from "lucide-react"
import { useToggleConversationAIMutation } from "modules/conversations/hooks/useToggleConversationAIMutation"
import { useTranslation } from "react-i18next"
import { ConversationsResponseDataSchema } from "schema"
import { z } from "zod"

interface AIControlsProps {
  conversation: z.infer<typeof ConversationsResponseDataSchema>[number]
  aiToggleCooldowns: Record<string, number>
  onSetAiToggleCooldowns: (
    callback: (prev: Record<string, number>) => Record<string, number>,
  ) => void
  onSetInputs: (callback: (prev: Record<string, string>) => Record<string, string>) => void
  switchToManualDisabledTooltip?: string
}

export function AIControls({
  conversation,
  aiToggleCooldowns,
  onSetAiToggleCooldowns,
  onSetInputs,
  switchToManualDisabledTooltip,
}: AIControlsProps) {
  const { mutateAsync: toggleConversationAI } = useToggleConversationAIMutation(conversation.id)

  const { i18n, t } = useTranslation()

  const conversationId = conversation.id
  const aiDisabled = conversation.aiDisabled
  const aiToggleDisabled = Date.now() < (aiToggleCooldowns[conversationId] ?? 0)

  if (aiDisabled) {
    let aiDisabledUntilLabel: string | null = null

    if (conversation.aiDisabledPermanently) {
      aiDisabledUntilLabel = t("conversations.chat.agentStatus.off")
    } else if (conversation.aiDisabledUntil) {
      const aiDisabledUntil = new Date(conversation.aiDisabledUntil)
      const now = new Date()
      const aiDisabledUntilDateOptions: Intl.DateTimeFormatOptions = {
        day: "numeric",
        month: "long",
      }
      if (aiDisabledUntil.getFullYear() !== now.getFullYear()) {
        aiDisabledUntilDateOptions.year = "numeric"
      }
      const aiDisabledUntilTime = formatDate(aiDisabledUntil, "p")
      const aiDisabledUntilDate = new Intl.DateTimeFormat(
        i18n.language,
        aiDisabledUntilDateOptions,
      ).format(aiDisabledUntil)
      aiDisabledUntilLabel = isSameDay(aiDisabledUntil, now)
        ? t("conversations.chat.agentStatus.offUntil", {
            time: aiDisabledUntilTime,
          })
        : t("conversations.chat.agentStatus.offUntilWithDate", {
            time: aiDisabledUntilTime,
            date: aiDisabledUntilDate,
          })
    }

    if (aiDisabledUntilLabel) {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="secondary" className="w-auto min-w-32 justify-between">
              <span>{aiDisabledUntilLabel}</span>
              <ChevronUp className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuLabel>{t("conversations.chat.manualControl.title")}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={async () => {
                await toggleConversationAI({ aiDisabled: false })
                onSetAiToggleCooldowns((prev) => ({
                  ...prev,
                  [conversationId]: Date.now() + 10000,
                }))
                onSetInputs((prev) => ({
                  ...prev,
                  [conversationId]: "",
                }))
              }}
            >
              {t("conversations.chat.manualControl.switchToAI")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={async () => {
                await toggleConversationAI({ aiDisabled: true })
              }}
            >
              {t("conversations.chat.manualControl.extend")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={async () => {
                await toggleConversationAI({ aiDisabled: true, permanent: true })
              }}
            >
              {t("conversations.chat.manualControl.permanent")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }

    return (
      <Button
        type="button"
        size="default"
        variant="secondary"
        disabled={aiToggleDisabled}
        onClick={async () => {
          await toggleConversationAI({ aiDisabled: false })
          onSetAiToggleCooldowns((prev) => ({
            ...prev,
            [conversationId]: Date.now() + 10000,
          }))
          onSetInputs((prev) => ({
            ...prev,
            [conversationId]: "",
          }))
        }}
      >
        <Bot />
        <span>{t("conversations.chat.manualControl.switchToAI")}</span>
      </Button>
    )
  } else {
    const disabled = aiToggleDisabled || Boolean(switchToManualDisabledTooltip)
    const switchToManualButton = (
      <Button
        type="button"
        size="default"
        variant="secondary"
        disabled={disabled}
        onClick={() => {
          toggleConversationAI({ aiDisabled: true })
        }}
      >
        <BotOff size={16} />
        <span>{t("conversations.chat.manualControl.switchToManual")}</span>
      </Button>
    )

    if (!switchToManualDisabledTooltip) return switchToManualButton

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">{switchToManualButton}</span>
        </TooltipTrigger>
        <TooltipContent>{switchToManualDisabledTooltip}</TooltipContent>
      </Tooltip>
    )
  }
}
